// This file is only used for Capacitor builds. It is used to manage push notifications for devices.
import { boot } from 'quasar/wrappers';
import { axios } from '@boot/axios';

// Dynamically import Capacitor plugins only on mobile platforms
let PushNotifications,
    Device,
    Browser,
    App,
    Camera,
    CameraResultType,
    CameraSource;
export default boot(async ({ app, store, router }) => {
    if (process.env.MODE === 'capacitor') {
        // Conditionally import Capacitor plugins only on mobile platforms
        PushNotifications = (await import('@capacitor/push-notifications'))
            .PushNotifications;
        Device = (await import('@capacitor/device')).Device;
        Browser = (await import('@capacitor/browser')).Browser;
        App = (await import('@capacitor/app')).App;
        Camera = (await import('@capacitor/camera')).Camera;
        CameraResultType = (await import('@capacitor/camera')).CameraResultType;
        CameraSource = (await import('@capacitor/camera')).CameraSource;

        // Define the PushNotificationManager class
        class PushNotificationManager {
            constructor() {
                if (process.env.MODE !== 'capacitor') {
                    throw new Error(
                        'PushNotificationManager can only be used in Capacitor builds.'
                    );
                }
            }

            /**
             * Initialize push notification manager
             */
            async initialize() {
                try {
                    await PushNotifications.addListener(
                        'registration',
                        async token => {
                            // Note: Need to import the store dynamically to avoid circular dependency
                            const { useBannersStore } = await import(
                                '@stores/banners'
                            );
                            const bannersStore = useBannersStore();
                            try {
                                const { identifier: deviceId } =
                                    await Device.getId();
                                const { platform } = await Device.getInfo();

                                await axios.post(
                                    'push_notifications/register/',
                                    {
                                        mobile_device_token: token.value,
                                        mobile_device_id: deviceId,
                                        platform: platform
                                    },
                                    {
                                        'axios-retry': { retries: 3 }
                                    }
                                );

                                console.info(
                                    'Push Notifications - Successfully registered device'
                                );

                                await bannersStore.dismissPushNotificationErrorBannerAction();
                            } catch (error) {
                                console.error(
                                    `Failed to register push notification token: ${error}`
                                );
                                await bannersStore.displayPushNotificationErrorBannerAction();
                            }
                        }
                    );

                    await PushNotifications.addListener(
                        'registrationError',
                        err => {
                            console.error(`Error on push registration: ${err}`);
                        }
                    );

                    await this.requestPermissions();
                } catch (error) {
                    console.error(
                        `Failed to initialize push notifications: ${error}`
                    );
                }
            }

            /**
             * Request device permissions
             */
            async requestPermissions() {
                let permStatus = await PushNotifications.checkPermissions();

                if (permStatus.receive === 'prompt') {
                    permStatus = await PushNotifications.requestPermissions();
                }

                if (permStatus.receive !== 'granted') {
                    console.error('User denied permissions!');
                }
            }

            /**
             * Register device for push notifications
             */
            async registerDevice() {
                await PushNotifications.register();
            }

            /**
             * Unregister device from push notifications
             */
            async unregisterDevice() {
                await PushNotifications.unregister();
                console.info('Push Notifications - Unregistered device');
            }
        }

        // Camera functions
        const capturePhoto = async () => {
            const image = await Camera.getPhoto({
                quality: 90,
                allowEditing: false,
                resultType: CameraResultType.Uri,
                source: CameraSource.Camera
            });
            // The entire image object is returned with the following properties:
            // webPath: the URL pointing to the image
            // base64String: the image as a base64 string
            // dataUrl: the image as a data URL
            return image;
        };

        const selectMedia = async () => {
            const media = await Camera.getPhoto({
                quality: 90,
                allowEditing: false,
                resultType: CameraResultType.Uri,
                source: CameraSource.Photos
            });

            let mimeType;
            if (media.webPath?.match(/\.(mov|mp4)$/i)) {
                mimeType = 'video/mp4';
            }

            return { webPath: media.webPath, mimeType };
        };

        // Open URL in browser
        const openUrl = async ({ url }) => {
            await Browser.open({ url });
        };

        // Handle deep links
        App.addListener('appUrlOpen', async event => {
            // Handle OAuth complete
            if (event.url.startsWith('iotorqlean://oauth/complete/')) {
                const params = new URL(event.url);
                const authCode = params.searchParams.get('code');

                // Close in-app browser
                await Browser.close();

                // Redirect to OAuth complete page to process the code
                router.push({
                    name: 'oauth_complete',
                    query: { code: authCode }
                });
            }
        });

        const instance = new PushNotificationManager();
        // Add instances to Vue app global properties
        app.config.globalProperties.$pushNotificationManager = instance;
        app.config.globalProperties.$openUrl = openUrl;
        app.config.globalProperties.$capturePhoto = capturePhoto;
        app.config.globalProperties.$selectMedia = selectMedia;
        // Add instances to Vuex rootState for access in store actions
        store.state.$pushNotificationManager = instance;

        // Enable edge-to-edge display on Android
        const deviceInfo = await Device.getInfo();
        if (deviceInfo.platform === 'android') {
            const { EdgeToEdge } = await import(
                '@capawesome/capacitor-android-edge-to-edge-support'
            );

            App.addListener('ready', () => {
                try {
                    EdgeToEdge.enable();
                } catch (error) {
                    console.error('Failed to enable edge-to-edge display:', error);
                }
            });
        }
    }
});
